import { KeyRound, Phone, Smartphone } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ThemeSwitcher } from '@/theme/ThemeSwitcher';

import { useAuth } from './AuthProvider';

type Mode = 'pin' | 'otp';

/**
 * Login screen. Two paths:
 *   - PIN: phone (10 digits) + 4-digit PIN → instant login. The path
 *     manager + cashier use day-to-day. (Cashiers won't see much in
 *     this panel — RoleGuard sends them to /not-authorised.)
 *   - OTP: phone → server SMS → enter OTP. Slower but doesn't require
 *     a PIN to be set first.
 *
 * Onboarding new tenants is mobile-only — no flow here.
 */
export function LoginPage() {
  const { status, loginPin, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>('pin');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (status === 'authed') {
    const to = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={to} replace />;
  }

  const phoneClean = phone.replace(/\D/g, '');

  const onLoginPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneClean.length !== 10) return toast.error('Enter a 10-digit phone');
    if (pin.length !== 4) return toast.error('Enter your 4-digit PIN');
    setBusy(true);
    try {
      await loginPin(phoneClean, pin);
      navigate('/', { replace: true });
    } catch (e) {
      toast.error((e as Error).message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const onSendOtp = async () => {
    if (phoneClean.length !== 10) return toast.error('Enter a 10-digit phone');
    setBusy(true);
    try {
      const { devOtp } = await sendOtp(phoneClean);
      setOtpSent(true);
      if (devOtp) {
        toast.success(`Dev OTP: ${devOtp}`, { duration: 8000 });
      } else {
        toast.success('OTP sent — check your messages');
      }
    } catch (e) {
      toast.error((e as Error).message || 'Could not send OTP');
    } finally {
      setBusy(false);
    }
  };

  const onVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setBusy(true);
    try {
      await verifyOtp(phoneClean, otp);
      navigate('/', { replace: true });
    } catch (e) {
      toast.error((e as Error).message || 'Could not verify OTP');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* tiny header — theme switcher only */}
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-brand-500 grid place-items-center text-white font-bold">
            V
          </div>
          <span className="font-semibold text-ink">New Vishwas Electronics</span>
        </div>
        <ThemeSwitcher />
      </header>

      <main className="flex-1 grid place-items-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold text-ink">Welcome back</h1>
            <p className="text-sm text-ink-soft mt-2">
              Sign in to manage your shop's books, stock and reports.
            </p>
          </div>

          <Card>
            {/* mode tabs */}
            <div className="grid grid-cols-2 mb-5 rounded-xl bg-surface-2 p-1">
              <button
                onClick={() => setMode('pin')}
                className={
                  'h-9 rounded-lg text-sm font-medium transition-colors ' +
                  (mode === 'pin'
                    ? 'bg-surface text-ink shadow-sm'
                    : 'text-ink-soft hover:text-ink')
                }
              >
                PIN
              </button>
              <button
                onClick={() => setMode('otp')}
                className={
                  'h-9 rounded-lg text-sm font-medium transition-colors ' +
                  (mode === 'otp'
                    ? 'bg-surface text-ink shadow-sm'
                    : 'text-ink-soft hover:text-ink')
                }
              >
                OTP
              </button>
            </div>

            {mode === 'pin' ? (
              <form onSubmit={onLoginPin} className="space-y-4">
                <Input
                  label="Phone"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="98765 43210"
                  iconLeft={<Phone className="h-4 w-4" />}
                />
                <Input
                  label="4-digit PIN"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  iconLeft={<KeyRound className="h-4 w-4" />}
                />
                <Button type="submit" loading={busy} block>
                  Sign in
                </Button>
              </form>
            ) : !otpSent ? (
              <div className="space-y-4">
                <Input
                  label="Phone"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="98765 43210"
                  iconLeft={<Phone className="h-4 w-4" />}
                />
                <Button onClick={onSendOtp} loading={busy} block>
                  Send OTP
                </Button>
              </div>
            ) : (
              <form onSubmit={onVerifyOtp} className="space-y-4">
                <Input
                  label={`OTP sent to +91 ${phoneClean}`}
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="• • • • • •"
                  iconLeft={<Smartphone className="h-4 w-4" />}
                />
                <Button type="submit" loading={busy} block>
                  Verify & continue
                </Button>
                <button
                  type="button"
                  className="text-sm text-ink-soft hover:text-ink w-full"
                  onClick={() => setOtpSent(false)}
                >
                  Use a different number
                </button>
              </form>
            )}
          </Card>

          <p className="text-xs text-ink-soft text-center mt-6">
            New to New Vishwas Electronics? Set up your shop on the mobile app first — this panel
            is for owners, managers and accountants only.
          </p>
        </div>
      </main>
    </div>
  );
}
